import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackCleavertapEvent } from "../../utils/analyics/analyticsHelper";
import { ANALYTICS_EVENTS } from "../../utils/analyics/analyticsEvents";
import { EXCLUDED_ROUTES_FOR_ANALYTICS } from "../../utils/analyics/analyticsConstant";
import { LOCALSTORAGE_KEY } from "../../utils/Constants";

export default function ScrollTracker() {
  const location = useLocation();

  const startTimeRef = useRef<number>(Date.now());
  const maxScrollRef = useRef<number>(0);
  const visibleItemsRef = useRef<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const isExcludedRoute = EXCLUDED_ROUTES_FOR_ANALYTICS.some((route) =>
      location.pathname.startsWith(route),
    );

    if (isExcludedRoute) return;
    startTimeRef.current = Date.now();
    maxScrollRef.current = 0;
    visibleItemsRef.current.clear();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute("data-scroll-id");
            if (id) {
              visibleItemsRef.current.add(id);
            }
          }
        });
      },
      {
        threshold: 0.4,
      },
    );

    const observeItems = () => {
      const elements = document.querySelectorAll("[data-scroll-id]");
      elements.forEach((el) => {
        observerRef.current?.observe(el);
      });
    };

    observeItems();
    const mutationObserver = new MutationObserver(() => {
      observeItems();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
    return () => {
      observerRef.current?.disconnect();
      mutationObserver.disconnect();
    };
  }, [location.pathname]);

  useEffect(() => {
    let ticking = false;
    const isExcludedRoute = EXCLUDED_ROUTES_FOR_ANALYTICS.some((route) =>
      location.pathname.startsWith(route),
    );

    if (isExcludedRoute) return;

    const updateScroll = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const fullHeight = document.documentElement.scrollHeight - windowHeight;

      if (fullHeight > 0) {
        const scrollPercent = Math.round((scrollTop / fullHeight) * 100);

        if (scrollPercent > maxScrollRef.current) {
          maxScrollRef.current = scrollPercent;
        }
      }

      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScroll);
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);

      const timeOnPage = Math.round((Date.now() - startTimeRef.current) / 1000);

      if (timeOnPage < 3) return;

      const maxScroll = maxScrollRef.current;
      const itemsViewed = visibleItemsRef.current.size;

      const engagementLevel =
        maxScroll >= 75 && timeOnPage > 60 && itemsViewed >= 3
          ? "high"
          : maxScroll >= 50 || itemsViewed >= 2
            ? "medium"
            : "low";

      trackCleavertapEvent(ANALYTICS_EVENTS.Scroll_Engaged, {
        User_ID:
          JSON.parse(localStorage.getItem(LOCALSTORAGE_KEY.USERINFO) ?? "{}")
            ?.id ?? "Guest User",
        Page_Name: location.pathname,
        Max_Scroll_Percentage: maxScroll,
        Scroll_Depth_Items: itemsViewed,
        Time_on_Page_Seconds: timeOnPage,
        Engagement_Level: engagementLevel,
      });
    };
  }, [location.pathname]);

  return null;
}
